/* ── Country data + decoration helpers for richer album layouts ──────── */

import { ALBUM_SECTIONS } from './albumData';

/* Each non-FWC group has 4 countries, 80 stickers / 4 = 20 per country.
   Order matches the official 2026 World Cup draw and the subtitle order in
   ALBUM_SECTIONS. Cambiar este orden invalida los álbumes existentes
   (sticker N pasa a ser otro país). */
const SECTION_COUNTRIES = {
  FWC: null,
  A: ['México', 'Sudáfrica', 'Corea del Sur', 'República Checa'],
  B: ['Canadá', 'Bosnia y Herzegovina', 'Catar', 'Suiza'],
  C: ['Brasil', 'Marruecos', 'Haití', 'Escocia'],
  D: ['USA', 'Paraguay', 'Australia', 'Turquía'],
  E: ['Alemania', 'Curazao', 'Costa de Marfil', 'Ecuador'],
  F: ['Países Bajos', 'Japón', 'Suecia', 'Túnez'],
  G: ['Bélgica', 'Egipto', 'Irán', 'Nueva Zelanda'],
  H: ['España', 'Cabo Verde', 'Arabia Saudita', 'Uruguay'],
  I: ['Francia', 'Senegal', 'Irak', 'Noruega'],
  J: ['Argentina', 'Argelia', 'Austria', 'Jordania'],
  K: ['Portugal', 'RD del Congo', 'Uzbekistán', 'Colombia'],
  L: ['Inglaterra', 'Croacia', 'Ghana', 'Panamá'],
};

export const COUNTRY_CODE = {
  'México': 'MEX', 'Sudáfrica': 'RSA', 'Corea del Sur': 'KOR', 'República Checa': 'CZE',
  'Canadá': 'CAN', 'Bosnia y Herzegovina': 'BIH', 'Catar': 'QAT', 'Suiza': 'SUI',
  'Brasil': 'BRA', 'Marruecos': 'MAR', 'Haití': 'HAI', 'Escocia': 'SCO',
  'USA': 'USA', 'Paraguay': 'PAR', 'Australia': 'AUS', 'Turquía': 'TUR',
  'Alemania': 'GER', 'Curazao': 'CUW', 'Costa de Marfil': 'CIV', 'Ecuador': 'ECU',
  'Países Bajos': 'NED', 'Japón': 'JPN', 'Suecia': 'SWE', 'Túnez': 'TUN',
  'Bélgica': 'BEL', 'Egipto': 'EGY', 'Irán': 'IRN', 'Nueva Zelanda': 'NZL',
  'España': 'ESP', 'Cabo Verde': 'CPV', 'Arabia Saudita': 'KSA', 'Uruguay': 'URU',
  'Francia': 'FRA', 'Senegal': 'SEN', 'Irak': 'IRQ', 'Noruega': 'NOR',
  'Argentina': 'ARG', 'Argelia': 'ALG', 'Austria': 'AUT', 'Jordania': 'JOR',
  'Portugal': 'POR', 'RD del Congo': 'COD', 'Uzbekistán': 'UZB', 'Colombia': 'COL',
  'Inglaterra': 'ENG', 'Croacia': 'CRO', 'Ghana': 'GHA', 'Panamá': 'PAN',
};

/* Approximate national flag colors — used only as visual identifiers, not
   for accuracy. */
export const COUNTRY_COLOR = {
  'México': '#006847', 'Sudáfrica': '#007A4D', 'Corea del Sur': '#003478', 'República Checa': '#11457E',
  'Canadá': '#FF0000', 'Bosnia y Herzegovina': '#002F6C', 'Catar': '#8A1538', 'Suiza': '#DA291C',
  'Brasil': '#009C3B', 'Marruecos': '#C1272D', 'Haití': '#00209F', 'Escocia': '#005EB8',
  'USA': '#3C3B6E', 'Paraguay': '#D52B1E', 'Australia': '#012169', 'Turquía': '#E30A17',
  'Alemania': '#1F1F1F', 'Curazao': '#002B7F', 'Costa de Marfil': '#F77F00', 'Ecuador': '#D4A800',
  'Países Bajos': '#FF6300', 'Japón': '#BC002D', 'Suecia': '#006AA7', 'Túnez': '#E70013',
  'Bélgica': '#E1C02D', 'Egipto': '#CE1126', 'Irán': '#239F40', 'Nueva Zelanda': '#1F1F1F',
  'España': '#AA151B', 'Cabo Verde': '#003893', 'Arabia Saudita': '#006C35', 'Uruguay': '#0038A8',
  'Francia': '#0055A4', 'Senegal': '#00853F', 'Irak': '#CE1126', 'Noruega': '#BA0C2F',
  'Argentina': '#75AADB', 'Argelia': '#006233', 'Austria': '#ED2939', 'Jordania': '#000000',
  'Portugal': '#006600', 'RD del Congo': '#007FFF', 'Uzbekistán': '#0099B5', 'Colombia': '#B88500',
  'Inglaterra': '#1E468A', 'Croacia': '#171796', 'Ghana': '#CC9900', 'Panamá': '#005AA7',
};

/** Mapa inverso código (3 letras) → color de bandera. */
export const COLOR_BY_CODE = (() => {
  const map = {};
  for (const [name, code] of Object.entries(COUNTRY_CODE)) {
    map[code] = COUNTRY_COLOR[name];
  }
  return map;
})();

/** Get country slices for a section. Returns null for FWC. */
export function getSectionCountries(section) {
  const countries = SECTION_COUNTRIES[section.id];
  if (!countries) return null;
  const stickersPerCountry = (section.to - section.from + 1) / countries.length;
  return countries.map((country, idx) => ({
    name: country,
    code: COUNTRY_CODE[country] || country.slice(0, 3).toUpperCase(),
    color: COUNTRY_COLOR[country] || '#666',
    from: Math.round(section.from + idx * stickersPerCountry),
    to: Math.round(section.from + (idx + 1) * stickersPerCountry - 1),
  }));
}

/** Find the section + country a sticker belongs to. */
export function countryForSticker(n) {
  for (const section of ALBUM_SECTIONS) {
    if (n >= section.from && n <= section.to) {
      const countries = getSectionCountries(section);
      if (!countries) return { section, country: null, code: 'FWC', color: '#666' };
      for (const c of countries) {
        if (n >= c.from && n <= c.to) return { section, country: c, code: c.code, color: c.color };
      }
    }
  }
  return null;
}

/* ── Mock player names (deterministic, for demo purposes) ──────────── */

const FIRST_NAMES = [
  'L.', 'M.', 'A.', 'D.', 'C.', 'F.', 'J.', 'P.', 'R.', 'S.',
  'T.', 'V.', 'N.', 'O.', 'E.', 'B.', 'H.', 'K.', 'I.', 'G.',
];
const LAST_NAMES = [
  'García', 'Silva', 'Müller', 'Tanaka', 'Khan', 'Smith', 'Petrov', 'Rossi',
  'López', 'Kim', 'Hassan', 'Brown', 'Costa', 'Sato', 'Martínez', 'Schmidt',
  'Diallo', 'Park', 'Hernández', 'Johansson', 'Patel', 'Yamamoto', 'Ahmed',
  'Pérez', 'Gómez', 'Wilson', 'Novak', 'Bauer', 'Sánchez', 'Andersson',
];

export function mockPlayerName(n) {
  const f = FIRST_NAMES[(n * 13) % FIRST_NAMES.length];
  const l = LAST_NAMES[(n * 7 + 3) % LAST_NAMES.length];
  return `${f} ${l}`;
}

/* ── Mock sticker "photo" — gradient swatches keyed off country color ─ */

export function mockPhotoStyle(n) {
  const info = countryForSticker(n);
  const base = info?.color || '#888';
  // Two-stop radial gradient that suggests a face/portrait silhouette
  return {
    background: `radial-gradient(circle at 35% 28%, ${base}cc 0%, ${base} 55%, #00000033 100%)`,
  };
}
